import { useState } from 'react'
import { api } from '../api/client'
import type {
  Comparison,
  DimensionBreakdown,
  EventFinding,
  EvidencePackage,
  FunnelRate,
  MetricFinding,
} from '../api/types'
import { useAsync } from '../api/useReport'
import { Banner } from '../components/Banner'
import { FunnelReport } from '../components/FunnelReport'
import { formatChange, formatDate, formatMetric } from '../format'

interface Props {
  propertyId?: string
  /** Which day to narrate. Omitted means the most recent settled one. */
  reportDate?: string
  onBack?: () => void
}

const ARROW = { up: '↑', down: '↓', flat: '→' } as const

function tone(percent: number): 'up' | 'down' | 'flat' {
  if (percent > 0) return 'up'
  if (percent < 0) return 'down'
  return 'flat'
}

function count(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: value < 10 ? 2 : 0 })
}

/** Metric-aware value: a rate keeps its decimal, a volume gets thousands separators. */
function value(metric: string, amount: number): string {
  return formatMetric(metric, amount, 'full')
}

/**
 * Share of a movement, as a whole percent.
 *
 * Rounds before testing for zero, so a contribution of -0.4% shows as "—" rather than
 * the nonsensical "-0%".
 */
function share(percent: number): string {
  const rounded = Math.round(percent)
  return rounded === 0 ? '—' : `${rounded}%`
}

/**
 * The morning briefing — the same evidence package the agent receives.
 *
 * Rendered from `/api/briefing` rather than a parallel UI-only endpoint, so what is
 * shown here and what gets narrated are guaranteed to be the same numbers.
 */
export function Briefing({ propertyId, reportDate, onBack }: Props) {
  const [refreshKey, setRefreshKey] = useState(0)

  const { data, error, loading } = useAsync<EvidencePackage>(
    (signal) => api.briefing({ propertyId, reportDate, refresh: refreshKey > 0 }, signal),
    [propertyId, reportDate, refreshKey],
    true,
    // The refresh counter is part of the key on purpose: asking for a rebuild must
    // miss, or the button would redraw the very package it was pressed to replace.
    `briefing:${propertyId ?? ''}:${reportDate ?? ''}:${refreshKey}`,
  )

  if (loading) {
    return (
      <div className="card">
        <p className="placeholder">Building the briefing… (first run fetches from GA4)</p>
      </div>
    )
  }
  if (error) return <Banner tone="critical">{error}</Banner>
  if (!data) return null

  return (
    <>
      {onBack && (
        <div className="context-bar">
          <button type="button" className="link-button link-button--back" onClick={onBack}>
            ← Back to the daybook
          </button>
        </div>
      )}

      <div className="briefing-head">
        <div>
          <h2 className="section__title">Morning briefing — {formatDate(data.report_date, 'long')}</h2>
          <p className="section__note">
            {data.site} · compared with the same weekday a week earlier · {data.ga4_tokens_used} GA4
            tokens
          </p>
        </div>
        <button type="button" className="button secondary" onClick={() => setRefreshKey((n) => n + 1)}>
          Refresh
        </button>
      </div>

      {!data.safe_to_interpret && (
        <Banner tone="critical">
          <strong>Data quality failed.</strong> These figures describe the collection, not the
          audience — treat them as unreliable.
        </Banner>
      )}

      {data.events.length > 0 && (
        <section className="section">
          <div className="section__head">
            <h2 className="section__title">Tracked events</h2>
            <span className="section__note">Sessions containing the event, not event count</span>
          </div>
          <div className="tiles">
            {data.events.map((event) => (
              <EventTile key={event.event_name} event={event} />
            ))}
          </div>
        </section>
      )}

      {data.funnel_reports.length > 0 && (
        <section className="section">
          <div className="section__head">
            <h2 className="section__title">Funnels</h2>
            <span className="section__note">
              Sessions reaching each step, split by where they entered and how they paid
            </span>
          </div>
          {data.funnel_reports.map((funnel) => (
            <FunnelReport key={funnel.name} funnel={funnel} />
          ))}
        </section>
      )}

      {data.funnels.length > 0 && (
        <section className="section">
          <div className="section__head">
            <h2 className="section__title">Funnel rates</h2>
            <span className="section__note">Computed locally from session counts</span>
          </div>
          <div className="tiles">
            {data.funnels.map((funnel) => (
              <FunnelTile key={`${funnel.from_event}-${funnel.to_event}`} funnel={funnel} />
            ))}
          </div>
        </section>
      )}

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">Key movements</h2>
          <span className="section__note">
            {data.findings.length === 0 ? 'Nothing moved enough to report' : 'Largest first'}
          </span>
        </div>
        {data.findings.length === 0 ? (
          <div className="card">
            <p className="placeholder">No metric moved beyond the reporting threshold.</p>
          </div>
        ) : (
          data.findings.map((finding) => <Finding key={finding.metric} finding={finding} />)
        )}
      </section>

      {data.suggested_follow_ups.length > 0 && (
        <section className="section">
          <div className="section__head">
            <h2 className="section__title">Suggested follow-up</h2>
          </div>
          <ul className="followups">
            {data.suggested_follow_ups.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">Data quality</h2>
          <span className="section__note">Checked before anything was interpreted</span>
        </div>
        <div className="findings">
          {data.data_quality.map((check) => (
            <div className="finding" key={check.name}>
              <div>
                <div className="finding__segment">{check.name.replace(/_/g, ' ')}</div>
                <div className="finding__detail">{check.detail}</div>
              </div>
              <span className={`quality-pill quality-pill--${check.status}`}>{check.status}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">Privacy audit</h2>
          <span className="section__note">What left this deployment</span>
        </div>
        <div className="card">
          <ul className="audit">
            {data.audit_lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      </section>
    </>
  )
}

function EventTile({ event }: { event: EventFinding }) {
  const direction = tone(event.headline.change_percent)
  return (
    <div className="tile">
      <span className="tile__label">{event.label}</span>
      <span className="tile__value">{count(event.current)}</span>
      <span className={`tile__delta tile__delta--${direction}`}>
        <span aria-hidden="true">{ARROW[direction]}</span>
        {formatChange(event.headline.change_percent)}
        <span className="tile__delta-note">sessions</span>
      </span>
      <Baselines supporting={event.supporting} />
    </div>
  )
}

function FunnelTile({ funnel }: { funnel: FunnelRate }) {
  const direction = tone(funnel.rate_change_percent)
  return (
    <div className="tile">
      <span className="tile__label">{funnel.label}</span>
      <span className="tile__value">{funnel.rate_percent.toFixed(3)}%</span>
      <span className={`tile__delta tile__delta--${direction}`}>
        <span aria-hidden="true">{ARROW[direction]}</span>
        {formatChange(funnel.rate_change_percent)}
        <span className="tile__delta-note">
          {count(funnel.to_sessions)} of {count(funnel.from_sessions)}
        </span>
      </span>
    </div>
  )
}

function Baselines({ supporting }: { supporting: Comparison[] }) {
  const available = supporting.filter((item) => item.available)
  if (available.length === 0) return null
  return (
    <ul className="baselines">
      {available.map((item) => (
        <li key={item.baseline}>
          <span>{item.baseline_label}</span>
          <span className={`baselines__value baselines__value--${tone(item.change_percent)}`}>
            {formatChange(item.change_percent)}
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * One finding, collapsed to its headline and baselines.
 *
 * Everything needed to judge whether a movement is real stays visible — the change,
 * and all four baselines. Only the *explanation* (which segments drove it) is behind
 * the disclosure, because that is what you read when you have decided to investigate.
 */
function Finding({ finding }: { finding: MetricFinding }) {
  const direction = tone(finding.headline.change_percent)
  const hasDetail = finding.breakdowns.length > 0

  const header = (
    <>
      <div className="finding-card__head">
        {hasDetail && <span className="breakdown__chevron" aria-hidden="true" />}
        <div>
          <span className={`significance significance--${finding.significance}`}>
            {finding.significance}
          </span>
          <h3 className="finding-card__title">{finding.label}</h3>
          <p className="finding-card__lead">
            <strong className={`tile__delta--${direction}`}>
              {formatChange(finding.headline.change_percent)}
            </strong>{' '}
            vs {finding.headline.baseline_label} · {value(finding.metric, finding.headline.previous)} →{' '}
            {value(finding.metric, finding.headline.current)}
          </p>
        </div>
        {hasDetail && (
          <span className="finding-card__more">
            {finding.breakdowns.length} breakdown{finding.breakdowns.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <Baselines supporting={finding.supporting} />

      {/* A caveat on the number itself, so it must not be hidden behind the toggle. */}
      {finding.baseline_conflict && (
        <p className="conflict">
          <span aria-hidden="true">! </span>
          {finding.baseline_conflict}
        </p>
      )}
    </>
  )

  // With nothing to reveal, a disclosure control would be a dead affordance.
  if (!hasDetail) {
    return (
      <article className={`card finding-card finding-card--${finding.significance}`}>{header}</article>
    )
  }

  return (
    <details className={`card finding-card finding-card--${finding.significance}`}>
      <summary className="finding-card__summary">{header}</summary>
      <div className="finding-card__detail">
        {finding.breakdowns.map((breakdown) => (
          <Breakdown key={breakdown.dimension} breakdown={breakdown} />
        ))}
      </div>
    </details>
  )
}

/**
 * A dimension breakdown, collapsed by default.
 *
 * Native `<details>` rather than hand-rolled state: keyboard operation, screen-reader
 * announcement and find-in-page all work without any of it being reimplemented.
 *
 * The summary carries the leading segment, so the collapsed row still answers "what
 * drove this" and only the supporting detail is hidden.
 */
function Breakdown({ breakdown }: { breakdown: DimensionBreakdown }) {
  const leader = breakdown.contributors[0]

  return (
    <details className="breakdown">
      <summary className="breakdown__summary">
        <span className="breakdown__chevron" aria-hidden="true" />
        <strong className="breakdown__dimension">By {breakdown.dimension}</strong>
        {leader && (
          <span className="breakdown__lead">
            {leader.segment}{' '}
            <span className={`tile__delta--${tone(leader.change_percent)}`}>
              {formatChange(leader.change_percent)}
            </span>
            {/* Rates have no additive share, so it is omitted rather than shown as 0%. */}
            {leader.contribution_percent !== 0 && (
              <>
                {' '}
                <span className="breakdown__share">
                  · explains {Math.abs(leader.contribution_percent).toFixed(0)}%
                </span>
              </>
            )}
          </span>
        )}
        {breakdown.approximate && <span className="breakdown__flag">approximate</span>}
      </summary>

      {breakdown.note && <p className="breakdown__note">{breakdown.note}</p>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Segment</th>
              <th scope="col">Change</th>
              <th scope="col">Explains</th>
              <th scope="col">Share</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.contributors.map((item) => (
              <tr key={item.segment}>
                <th scope="row">{item.segment}</th>
                <td className={`tile__delta--${tone(item.change_percent)}`}>
                  {formatChange(item.change_percent)}
                </td>
                {/* Rates carry no additive decomposition, so no share of the move. */}
                <td>{share(item.contribution_percent)}</td>
                <td>{item.share_of_current.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}
